import { DateBox, ScrollView, TextBox } from "devextreme-react";
import {
  Column,
  DataGrid,
  Item as DataGridItem,
  FilterRow,
  Toolbar,
} from "devextreme-react/data-grid";
import notify from "../../lib/notify";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadEmployteeEvaluationSummary } from "../../api/employeeEvaluation";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import { useScreenSize } from "../../utils/media-query";
import "./EvaluationSummary.scss";

const companyName = (id: number): string => {
  switch (id) {
    case 1:
      return "Invenio";
    case 2:
      return "Invenio QD";
    case 3:
      return "Quality & Human Services";
    case 4:
      return "IPSUPPORT";
    default:
      return "";
  }
};

export const EvaluationSummary = () => {
  const [schema, setSchema] = useState<
    {
      id: number;
      companyId: number;
      firstName: string;
      lastName: number;
      rate: number[];
      managerRate: number[];
      gradingMethod: string[];
      deadline: Date[];
      averageGradeSelf: number;
      AverageGradeManager: number;
      schemaNames: string[];
    }[]
  >([]);
  const { companyId } = useCompanyContext();
  const { user } = useAuth();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const [filter, setFilter] = useState("");
  const [from, setFrom] = useState<string>(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split("T")[0];
  });
  const { t } = useTranslation();
  const [to, setTo] = useState<string>(() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return lastDay.toISOString().split("T")[0];
  });

  useEffect(() => {
    FetchSchemaEvaluation();
  }, [from, to]);

  const FetchSchemaEvaluation = async () => {
    try {
      let company = 0;

      if (companyId === 0) {
        company = user?.companyId || 0;
      } else {
        company = companyId;
      }
      const response = await downloadEmployteeEvaluationSummary(
        company,
        from,
        to
      );
      if (response.isOk) {
        setSchema(response.data);
      } else {
        notify(t("An error occurred while processing your request.", "error"));
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    }
  };

  const uniqueSchemaWithMethod = Array.from(
    new Set(
      schema.flatMap((item) =>
        item.schemaNames.map((name, index) => {
          const method = item.gradingMethod[index];
          return name && method ? `${name}|${method}` : null;
        })
      )
    )
  ).filter(Boolean);

  const filteredSchema = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return schema;
    return schema.filter((row) => {
      const fullName = `${row.lastName ?? ""} ${row.firstName ?? ""}`
        .trim()
        .toLowerCase();
      return fullName.includes(q);
    });
  }, [schema, filter]);

  return (
    <ScrollView useNative>
      <div className="p-6">
        <div className="mb-2 text-2xl font-bold leading-none">
          {t("sheets.summary")}
        </div>
        <div className="mb-1 text-base">
          {t("evaluationSummary.headerText")}
        </div>

        {isMobile ? (
          <div className="eval-summary-mobile">
            <div className="eval-summary-mobile-controls">
              <TextBox
                value={filter}
                onValueChanged={(e) => setFilter(e.value ?? "")}
                placeholder={t("sheets.Search...")}
                showClearButton
              />
              <div className="eval-summary-mobile-dates">
                <DateBox
                  value={from}
                  onValueChanged={(e: any) => setFrom(e.value)}
                  displayFormat="dd.MM.yyyy"
                  placeholder={t("summary.startDate")}
                  label={t("summary.startDate")}
                  labelMode="static"
                />
                <DateBox
                  value={to}
                  onValueChanged={(e: any) => setTo(e.value)}
                  displayFormat="dd.MM.yyyy"
                  placeholder={t("summary.endDate")}
                  label={t("summary.endDate")}
                  labelMode="static"
                />
              </div>
            </div>

            <div className="eval-summary-mobile-list">
              {filteredSchema.length === 0 ? (
                <div className="eval-summary-mobile-empty">{t("No data")}</div>
              ) : (
                filteredSchema.map((row, rowIdx) => {
                  const fullName = `${row.lastName ?? ""} ${
                    row.firstName ?? ""
                  }`.trim();
                  return (
                    <div
                      key={`${row.id}-${rowIdx}`}
                      className="eval-summary-mobile-card"
                    >
                      <div className="eval-summary-mobile-card-header">
                        <div className="eval-summary-mobile-card-name">
                          {fullName || "—"}
                        </div>
                        <div className="eval-summary-mobile-card-company">
                          {companyName(row.companyId)}
                        </div>
                      </div>

                      {uniqueSchemaWithMethod.length > 0 && (
                        <div className="eval-summary-mobile-card-schemas">
                          {uniqueSchemaWithMethod.map((entry) => {
                            if (!entry) return null;
                            const [schemaName, method] = entry.split("|");
                            const idx = row.schemaNames.findIndex(
                              (n, i) =>
                                n === schemaName &&
                                row.gradingMethod[i] === method
                            );
                            if (idx === -1) return null;
                            const selfVal = row.rate[idx];
                            const mgrVal = row.managerRate[idx];
                            if (selfVal == null && mgrVal == null) return null;
                            return (
                              <div
                                key={`row-${row.id}-${entry}`}
                                className="eval-summary-mobile-schema-row"
                              >
                                <div className="eval-summary-mobile-schema-name">
                                  {schemaName}
                                </div>
                                <div className="eval-summary-mobile-schema-rates">
                                  <span>
                                    <b>{t("S")}:</b>{" "}
                                    {selfVal != null ? selfVal : "—"}
                                  </span>
                                  <span>
                                    <b>{t("M")}:</b>{" "}
                                    {mgrVal != null ? mgrVal : "—"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="eval-summary-mobile-card-averages">
                        <span>
                          <b>{t("summary.averageSelfRate")}:</b>{" "}
                          {row.averageGradeSelf ?? "—"}
                        </span>
                        <span>
                          <b>{t("summary.averageManagerRate")}:</b>{" "}
                          {(row as any).averageGradeManager ??
                            row.AverageGradeManager ??
                            "—"}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          <DataGrid
            dataSource={schema}
            keyExpr="id"
            showBorders
            allowColumnResizing
            allowColumnReordering
            columnAutoWidth
            searchPanel={{
              visible: true,
              placeholder: t("sheets.Search..."),
            }}
            paging={{
              pageSize: 10,
            }}
            pager={{
              visible: true,
              showInfo: true,
              showNavigationButtons: true,
            }}
          >
            <Toolbar>
              <DataGridItem
                location="before"
                locateInMenu="auto"
                name="searchPanel"
                cssClass="datagrid-search-panel-without-margin-left"
              />
              <DataGridItem location="after" locateInMenu="auto">
                <div className="flex gap-2">
                  <DateBox
                    value={from}
                    onValueChanged={(e: any) => setFrom(e.value)}
                    displayFormat="dd.MM.yyyy"
                    placeholder={t("summary.startDate")}
                    label={t("summary.startDate")}
                    labelMode="static"
                    width={150}
                  />
                  <DateBox
                    value={to}
                    onValueChanged={(e: any) => setTo(e.value)}
                    displayFormat="dd.MM.yyyy"
                    placeholder={t("summary.endDate")}
                    label={t("summary.endDate")}
                    labelMode="static"
                    width={150}
                  />
                </div>
              </DataGridItem>
            </Toolbar>
            <FilterRow visible />
            <Column
              dataField="lastName"
              dataType="string"
              caption={t("summary.lastName")}
              minWidth={200}
            />
            <Column
              dataField="firstName"
              dataType="string"
              caption={t("summary.firstName")}
              minWidth={200}
            />
            <Column
              dataField="companyId"
              dataType="string"
              caption={t("company")}
              customizeText={({ value }) => companyName(Number(value))}
              minWidth={150}
            />
            {uniqueSchemaWithMethod.map((entry) => {
              if (!entry) return null;

              const [schemaName, method] = entry.split("|");
              return (
                <React.Fragment key={`group-${entry}`}>
                  <Column
                    key={`self-${entry}`}
                    caption={`${schemaName} - ${t("S")}`}
                    cellRender={({ data }) => {
                      const idx = data.schemaNames.findIndex(
                        (n, i) =>
                          n === schemaName && data.gradingMethod[i] === method
                      );
                      return idx !== -1 && data.rate[idx] !== undefined
                        ? data.rate[idx]
                        : null;
                    }}
                  />
                  <Column
                    key={`manager-${entry}`}
                    caption={`${schemaName} - ${t("M")}`}
                    cellRender={({ data }) => {
                      const idx = data.schemaNames.findIndex(
                        (n, i) =>
                          n === schemaName && data.gradingMethod[i] === method
                      );
                      return idx !== -1 && data.managerRate[idx] !== undefined
                        ? data.managerRate[idx]
                        : null;
                    }}
                  />
                </React.Fragment>
              );
            })}

            <Column
              dataField="averageGradeSelf"
              dataType="number"
              caption={t("summary.averageSelfRate")}
              alignment="left"
              minWidth={100}
            />
            <Column
              dataField="averageGradeManager"
              dataType="number"
              caption={t("summary.averageManagerRate")}
              alignment="left"
              minWidth={100}
            />
          </DataGrid>
        )}
      </div>
    </ScrollView>
  );
};
